import React, { useState, useEffect, useRef } from 'react'
import { API_URL, API_HOST } from '../../config/api'
import { Input, message as antMessage, Badge } from 'antd'
import { CloseOutlined, SendOutlined, PictureOutlined, MinusOutlined, MessageOutlined } from '@ant-design/icons'
import { io, Socket } from 'socket.io-client'
import { useLanguage } from '../../contexts/LanguageContext'

interface Message {
  id: string
  conversation_id: string
  sender_id: string
  sender_email: string
  sender_type: 'user' | 'agent'
  content: string
  message_type: 'text' | 'image'
  file_url?: string
  is_read: boolean
  created_at: string
  sender?: {
    id: string
    email: string
    name: string
  }
}

interface GuestRealTimeChatWindowProps {
  onClose: () => void
  onMinimize?: () => void
  isMinimized?: boolean
  guestId: string // 访客唯一标识
  onUnreadCountChange?: (count: number) => void // 未读数变化回调
}

const GuestRealTimeChatWindow: React.FC<GuestRealTimeChatWindowProps> = ({ 
  onClose, 
  onMinimize, 
  isMinimized,
  guestId,
  onUnreadCountChange
}) => {
  const { t } = useLanguage()
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [socket, setSocket] = useState<Socket | null>(null)
  const [uploading, setUploading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // 初始化 Socket.IO 连接（访客模式）
  useEffect(() => {
    const newSocket = io(API_HOST)
    setSocket(newSocket)

    return () => {
      newSocket.close()
    }
  }, [])

  // 获取或创建访客会话
  useEffect(() => {
    const initGuestConversation = async () => {
      try {
        const response = await fetch(`${API_URL}/chat/guest/conversation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            guestId
          })
        })
        const data = await response.json()

        if (data.success) {
          setConversationId(data.data.id)
          
          // 加入会话房间
          if (socket) {
            socket.emit('join_conversation', data.data.id)
          }

          // 获取历史消息
          fetchMessages(data.data.id)
        }
      } catch (error) {
        console.error('Failed to init guest conversation:', error)
      }
    }

    if (socket) {
      initGuestConversation()
    }
  }, [socket, guestId])

  // 监听新消息
  useEffect(() => {
    if (!socket) return

    socket.on('new_message', (message: Message) => {
      setMessages(prev => [...prev, message])
      
      // 如果是客服发送的消息且窗口最小化，增加未读数
      if (message.sender_type === 'agent' && isMinimized) {
        const newCount = unreadCount + 1
        setUnreadCount(newCount)
        // 通知父组件未读数变化
        if (onUnreadCountChange) {
          onUnreadCountChange(newCount)
        }
      }
      
      // 播放提示音（如果是客服发送的消息）
      if (message.sender_type === 'agent') {
        playNotificationSound()
      }
    })

    socket.on('messages_read', ({ readBy }) => {
      if (readBy === 'agent') {
        // 客服已读，更新消息状态
        setMessages(prev => prev.map(msg => 
          msg.sender_type === 'user' ? { ...msg, is_read: true } : msg
        ))
      }
    })

    // 监听单条消息已读状态更新
    socket.on('message_read_status', ({ messageId, isRead }) => {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, is_read: isRead } : msg
      ))
    })

    return () => {
      socket.off('new_message')
      socket.off('messages_read')
      socket.off('message_read_status')
    }
  }, [socket, isMinimized])

  // 窗口展开时清除未读数并标记已读
  useEffect(() => {
    if (!isMinimized && conversationId) {
      // 清除本地未读数
      if (unreadCount > 0) {
        setUnreadCount(0)
        if (onUnreadCountChange) {
          onUnreadCountChange(0)
        }
      }
      // 始终调用 markAsRead，让后端更新消息已读状态
      markAsRead()
    }
  }, [isMinimized, conversationId])

  // 获取消息后也标记已读（确保客服发送的消息被标记为已读）
  useEffect(() => {
    if (messages.length > 0 && conversationId && !isMinimized) {
      // 检查是否有未读的客服消息
      const hasUnreadAgentMessages = messages.some(
        msg => msg.sender_type === 'agent' && !msg.is_read
      )
      if (hasUnreadAgentMessages) {
        markAsRead()
      }
    }
  }, [messages, conversationId, isMinimized])

  // 获取历史消息
  const fetchMessages = async (convId: string) => {
    try {
      const response = await fetch(`${API_HOST}/api/chat/guest/messages/${convId}`)
      const data = await response.json()

      if (data.success) {
        setMessages(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error)
    }
  }

  // 标记消息为已读
  const markAsRead = async () => {
    if (!conversationId) return

    try {
      await fetch(`${API_HOST}/api/chat/guest/mark-read/${conversationId}`, {
        method: 'POST'
      })
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  // 播放提示音
  const playNotificationSound = () => {
    const audio = new Audio('/notification.mp3')
    audio.play().catch(err => console.log('Failed to play sound:', err))
  }

  // 发送消息
  const handleSend = async () => {
    if (!inputValue.trim() || !conversationId) return

    try {
      const response = await fetch(`${API_URL}/chat/guest/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId,
          guestId,
          content: inputValue,
          messageType: 'text'
        })
      })

      const data = await response.json()

      if (data.success) {
        setInputValue('')
      } else {
        antMessage.error(t('customerService.sendFailed'))
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      antMessage.error(t('customerService.sendFailed'))
    }
  }

  // 上传图片
  const handleImageUpload = async (file: File) => {
    if (!conversationId) return

    setUploading(true)

    try {
      const formData = new FormData()
      formData.append('image', file)
      formData.append('conversationId', conversationId)
      formData.append('guestId', guestId)

      const response = await fetch(`${API_URL}/chat/guest/upload-image`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        antMessage.success(t('customerService.sendImageSuccess'))
      } else {
        antMessage.error(t('customerService.sendImageFailed'))
      }
    } catch (error) {
      console.error('Failed to upload image:', error)
      antMessage.error(t('customerService.sendImageFailed'))
    } finally {
      setUploading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 bg-[#f0b90b] rounded-full p-4 cursor-pointer shadow-lg z-50"
        onClick={onMinimize}
      >
        <Badge count={unreadCount} offset={[-5, 5]}>
          <SendOutlined className="text-black text-xl" />
        </Badge>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 w-[380px] h-[550px] bg-gradient-to-b from-[#1a1f2e] to-[#0f1419] rounded-3xl shadow-2xl flex flex-col border border-gray-700/50 z-50 overflow-hidden backdrop-blur-xl">
      {/* 头部 */}
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#f0b90b]/10 to-transparent border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#f0b90b] to-[#d4a00a] rounded-full flex items-center justify-center shadow-lg">
            <MessageOutlined className="text-black text-lg" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">{t('customerService.title')}</h3>
            <p className="text-xs text-gray-400">{t('customerService.welcomeMessage')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onMinimize && (
            <button
              onClick={onMinimize}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <MinusOutlined className="text-sm" />
            </button>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
              <MessageOutlined className="text-gray-600 text-2xl" />
            </div>
            <p className="text-gray-500 text-sm">{t('order.noOrders')}</p>
            <p className="text-gray-600 text-xs mt-1">{t('customerService.welcomeMessage')}</p>
          </div>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
          >
            <div className="flex flex-col max-w-[75%]">
              {message.sender_type === 'agent' && (
                <span className="text-xs text-gray-500 mb-1.5 ml-3 font-medium">
                  {message.sender_email}
                </span>
              )}
              <div
                className={`rounded-2xl px-4 py-3 shadow-lg ${
                  message.sender_type === 'user'
                    ? 'bg-gradient-to-br from-[#f0b90b] to-[#d4a00a] text-black'
                    : 'bg-gradient-to-br from-gray-800 to-gray-900 text-white border border-gray-700/50'
                }`}
              >
                {message.message_type === 'image' && message.file_url ? (
                  <img 
                    src={`${API_HOST}${message.file_url}`} 
                    alt="Uploaded" 
                    className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => window.open(`${API_HOST}${message.file_url}`, '_blank')}
                  />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                )}
                <div className="flex items-center justify-between mt-2 gap-2">
                  <p
                    className={`text-xs ${
                      message.sender_type === 'user' ? 'text-black/50' : 'text-gray-500'
                    }`}
                  >
                    {new Date(message.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  {message.sender_type === 'user' && (
                    <span className={`text-xs font-medium ${message.is_read ? 'text-blue-400' : 'text-black/40'}`}>
                      {message.is_read ? '✓✓' : '✓'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入框 */}
      <div className="px-4 py-4 bg-gradient-to-t from-[#0f1419] to-transparent border-t border-gray-700/50">
        <div className="flex gap-2 mb-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                handleImageUpload(file)
              }
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-10 h-10 flex items-center justify-center bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white rounded-xl transition-all border border-gray-700/50"
          >
            <PictureOutlined className="text-base" />
          </button>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('customerService.inputPlaceholder')}
            className="flex-1 h-10 bg-gray-800/80 border-gray-700/50 text-white placeholder-gray-500 rounded-xl hover:bg-gray-800 focus:bg-gray-800 transition-all"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#f0b90b] to-[#d4a00a] hover:from-[#d4a00a] hover:to-[#f0b90b] text-black rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SendOutlined className="text-base" />
          </button>
        </div>
        <p className="text-xs text-gray-500 text-center">
          {t('auth.pleaseLogin')}
        </p>
      </div>
    </div>
  )
}

export default GuestRealTimeChatWindow
